import { useEffect, useRef, useState, type Ref } from 'react';
import { Link } from 'react-router-dom';
import type { ScriptFeatures, ScriptStatus } from '../../../shared/types';
import type { SaveState } from '../Editor/useAutosave';
import { useMediaQuery } from '../../lib/useMediaQuery';
import { useTheme } from '../../lib/theme';
import Wordmark from '../Wordmark';
import { ThemeToggleButton } from '../ThemeToggle';
import Button from '../Button';
import StatusToggle from '../StatusToggle';
import FeaturesPanel from '../FeaturesPanel';
import { BriefPdfMenuButton } from '../Brief/BriefPdfSplitButton';
import DocState, { type PublishZone } from './DocState';
import PublishButton from '../Publish/PublishButton';
import type { StatusPillLabel } from './statusLabels';
import MobileEditorNav from './MobileEditorNav';
import DiffLegend from './DiffLegend';
import MoreMenu, { type MoreMenuGroup } from './MoreMenu';
import { Rail, RailButton, RailDivider } from './RailButton';
import { STATUS_PILL_LABELS } from './statusLabels';
import {
  CheckIcon,
  ExternalIcon,
  FieldsIcon,
  GearIcon,
  HistoryIcon,
  LinkIcon,
  PdfIcon,
  PhoneIcon,
  ResetIcon,
  ScratchIcon,
  ScissorsIcon,
  ThemeIcon,
} from './icons';

export interface Props {
  title?: string;
  mobilePane?: 'script' | 'scratch';
  onMobilePane?: (pane: 'script' | 'scratch') => void;
  onMobilePanel?: (key: string) => void;
  onOpenClientView?: () => void;
  mobilePanel?: string | null;
  kind: 'script' | 'brief';
  // zone 2
  saveState: SaveState;
  version: number;
  saveErrorMsg: string | null;
  onRetrySave: () => void;
  clientPresent: boolean;
  // zone 3
  status: ScriptStatus;
  onStatusChange: (s: ScriptStatus) => Promise<void> | void;
  // rail: panes
  scratchOpen: boolean;
  scratchCount: number;
  onToggleScratchpad: () => void;
  fieldsOpen?: boolean;
  onToggleFields?: () => void;
  fieldsButtonRef?: Ref<HTMLButtonElement>;
  features: ScriptFeatures;
  onFeaturesChange: (f: ScriptFeatures) => Promise<void> | void;
  onRotateSlug: () => Promise<void> | void;
  onOpenVersionHistory: () => void;
  /** Scripts only. Absent for briefs, so the item never shows there. */
  onMakeCutdown?: () => void;
  /** Why a cutdown is off ("Already 15 seconds or shorter"); null when on. */
  cutdownReason?: string | null;
  // rail: share
  shareUrl: string;
  linkShareable: boolean;
  onCopyShareUrl: () => Promise<void> | void;
  onDownloadPdf: () => void;
  onDownloadBriefPdf?: (variant: 'client' | 'full') => void;
  /** Null when there is nothing to shoot: the icon is absent, never dimmed.
   *  The page decides how it opens (new tab or in place, after a flush). */
  onOpenShootView: (() => void) | null;
  // more
  hasActiveSuggestion: boolean;
  onResetClientView: () => void;
  // compare mode
  compareMode: boolean;
  onStopComparing: () => void;
  // publish revisions (2A)
  /** Zone 2 says which version the client reads. */
  publishZone?: PublishZone;
  /** Publish shows only while the working copy differs from the published one. */
  showPublish?: boolean;
  publishBusyRound?: number | null;
  onPublish?: () => void;
  /** Pill wording ("Round 4" past v3) and the statuses the menu offers. */
  statusLabels?: (s: ScriptStatus) => StatusPillLabel;
  statusOptions?: ScriptStatus[];
  /** Client review group: Remind client (C2). */
  remind?: { enabled: boolean; line: string; onSelect: () => void };
  /** "Take the page down" in More, once the script has been published. */
  onTakeDown?: (() => void) | null;
}

const COPIED_MS = 1000;

/**
 * The editor's whole header: identity, document state, status and one icon
 * rail, on a single 52px row from md up and two short rows on a phone.
 *
 * One DOM at every width. Nothing here is duplicated per breakpoint, because
 * the popovers and the forwarded Fields ref have to exist exactly once —
 * widths only change which words are visible.
 */
export default function EditorTopBar(props: Props) {
  const {
  kind,
  saveState,
  version,
  saveErrorMsg,
  onRetrySave,
  clientPresent,
  status,
  onStatusChange,
  scratchOpen,
  scratchCount,
  onToggleScratchpad,
  fieldsOpen,
  onToggleFields,
  fieldsButtonRef,
  features,
  onFeaturesChange,
  onRotateSlug,
  onOpenVersionHistory,
  onMakeCutdown,
  cutdownReason,
  shareUrl,
  linkShareable,
  onCopyShareUrl,
  onDownloadPdf,
  onDownloadBriefPdf,
  onOpenShootView,
  hasActiveSuggestion,
  onResetClientView,
  compareMode,
  onStopComparing,
  publishZone,
  showPublish = false,
  publishBusyRound = null,
  onPublish,
  statusLabels = (s: ScriptStatus) => STATUS_PILL_LABELS[s],
  statusOptions,
  remind,
  onTakeDown,
  } = props;
  const isBrief = kind === 'brief';
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { theme, toggle: toggleTheme } = useTheme();
  // Zone 1 shows the toggle from lg up; below that the More menu carries it,
  // so the two are never both offering the same switch.
  const themeToggleInBar = useMediaQuery('(min-width: 1024px)');

  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | null>(null);
  useEffect(() => () => {
    if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
  }, []);

  async function copyLink() {
    await onCopyShareUrl();
    setCopied(true);
    if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
  }

  const moreGroups: MoreMenuGroup[] = [
    {
      label: isBrief ? 'This brief' : 'This script',
      items: [
        {
          key: 'client-page',
          label: 'Open client page',
          sub: 'See what they see',
          hint: 'New tab',
          icon: <ExternalIcon size={14} />,
          enabled: linkShareable,
          reason: 'Move past Draft to share',
          onSelect: () => window.open(shareUrl, '_blank', 'noopener'),
        },
        {
          key: 'history',
          label: 'Version history',
          icon: <HistoryIcon size={14} />,
          enabled: true,
          onSelect: onOpenVersionHistory,
        },
        ...(!isBrief && onMakeCutdown
          ? [
              {
                key: 'cutdown',
                label: 'Make a cutdown',
                sub: '60, 30 or 15 second versions',
                icon: <ScissorsIcon size={14} />,
                enabled: !cutdownReason,
                reason: cutdownReason ?? undefined,
                onSelect: onMakeCutdown,
              },
            ]
          : []),
        ...(themeToggleInBar
          ? []
          : [
              {
                key: 'theme',
                label: 'Dark theme',
                hint: theme === 'dark' ? 'On' : 'Off',
                icon: <ThemeIcon size={14} />,
                enabled: true,
                onSelect: toggleTheme,
              },
            ]),
      ],
    },
    {
      label: 'Client review',
      items: [
        ...(remind
          ? [
              {
                key: 'remind',
                label: 'Remind client',
                sub: remind.enabled ? remind.line : undefined,
                hint: 'Email',
                icon: <MailIcon />,
                enabled: remind.enabled,
                reason: remind.line,
                onSelect: remind.onSelect,
              },
            ]
          : []),
        {
          key: 'reset',
          label: 'Reset client view',
          sub: 'Discard their open suggestion',
          icon: <ResetIcon size={14} />,
          enabled: hasActiveSuggestion,
          reason: 'No open suggestion',
          danger: true,
          onSelect: onResetClientView,
        },
        ...(onTakeDown
          ? [
              {
                key: 'take-down',
                label: 'Take the page down',
                sub: 'Shows "being revised" until you publish',
                icon: <TakeDownIcon />,
                enabled: true,
                danger: true,
                onSelect: onTakeDown,
              },
            ]
          : []),
      ],
    },
  ];

  if (!isDesktop) return <MobileEditorNav {...props} theme={theme} toggleTheme={toggleTheme} />;

  return (
    <header
      data-print-keep
      className="border-b border-line bg-page/80 backdrop-blur sticky top-0 z-20"
    >
      <div className="max-w-7xl mx-auto px-3 md:px-8 flex flex-wrap md:flex-nowrap items-center md:h-[52px] md:gap-4">
        {/* Group A: who and what. Row one on a phone. */}
        <div className="flex items-center justify-between gap-3 w-full md:w-auto md:flex-1 min-w-0 h-11 md:h-auto">
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/admin"
              data-print-hide
              className="flex items-center gap-2 text-muted hover:text-fg transition-colors"
            >
              <span aria-hidden>←</span>
              <Wordmark size="sm" />
            </Link>
            <span className="hidden print:inline-block">
              <Wordmark size="sm" />
            </span>
            <span data-print-hide className="hidden lg:inline-flex">
              <ThemeToggleButton size="sm" theme={theme} toggle={toggleTheme} />
            </span>
          </div>
          <div
            data-print-hide
            className="flex-1 min-w-0 flex items-center gap-3.5 justify-end md:justify-start overflow-hidden"
          >
            <DocState
              state={saveState}
              version={version}
              errorMsg={saveErrorMsg}
              onRetry={onRetrySave}
              clientPresent={clientPresent}
              publish={publishZone}
            />
            {compareMode && <DiffLegend />}
          </div>
        </div>

        {/* Group B: what you can do. Row two on a phone. */}
        <div
          data-print-hide
          className="flex items-center justify-between gap-2 md:gap-3 w-full md:w-auto h-10 md:h-auto shrink-0"
        >
          {compareMode ? (
            // Comparing owns the whole screen, so the rail and the status pill
            // stand down: the only thing left to decide is whether to stop.
            <Button
              variant="secondary"
              size="sm"
              className="ml-auto h-[30px] py-0"
              onClick={onStopComparing}
            >
              Stop comparing
            </Button>
          ) : (
            <>
              {/* 2A: Publish sits immediately left of the status pill and
                  grows into the flexible zone, so the pill never moves. */}
              <div className="flex items-center gap-3 shrink-0">
                {showPublish && onPublish && (
                  <PublishButton
                    saveFailed={saveState === 'error'}
                    busyRound={publishBusyRound}
                    onClick={onPublish}
                  />
                )}
                <StatusToggle
                  current={status}
                  onChange={onStatusChange}
                  labels={statusLabels}
                  options={statusOptions}
                  hideLabelBelowMd
                />
              </div>
              <Rail>
                <RailButton
                  icon={<ScratchIcon />}
                  label="Scratchpad"
                  shortcut="⌘⇧S"
                  pressed={scratchOpen}
                  count={scratchOpen ? 0 : scratchCount}
                  disabled={status === 'approved' && !scratchOpen}
                  disabledReason="Not needed once approved"
                  onClick={onToggleScratchpad}
                />
                {isBrief && (
                  <RailButton
                    ref={fieldsButtonRef}
                    icon={<FieldsIcon />}
                    label="Fields"
                    haspopup="dialog"
                    pressed={fieldsOpen}
                    expanded={fieldsOpen}
                    onClick={onToggleFields}
                  />
                )}
                <FeaturesPanel
                  current={features}
                  onChange={onFeaturesChange}
                  onRotateSlug={onRotateSlug}
                  onOpenVersionHistory={onOpenVersionHistory}
                  trigger={({ open, toggle, ref }) => (
                    <RailButton
                      ref={ref}
                      icon={<GearIcon />}
                      label="Features"
                      haspopup="dialog"
                      pressed={open}
                      expanded={open}
                      onClick={toggle}
                    />
                  )}
                />

                <RailDivider />

                <RailButton
                  icon={copied ? <CheckIcon /> : <LinkIcon />}
                  label={copied ? 'Copied' : 'Copy client link'}
                  disabled={!linkShareable}
                  disabledReason="Move past Draft to share"
                  onClick={() => void copyLink()}
                />
                {isBrief && onDownloadBriefPdf ? (
                  <BriefPdfMenuButton onDownload={onDownloadBriefPdf} />
                ) : (
                  <RailButton
                    icon={<PdfIcon />}
                    label="Download PDF"
                    onClick={onDownloadPdf}
                  />
                )}
                {onOpenShootView && (
                  <RailButton icon={<PhoneIcon />} label="Shoot view" onClick={onOpenShootView} />
                )}

                <RailDivider />

                <MoreMenu groups={moreGroups} />
              </Rail>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function MailIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m4 7 8 6 8-6" />
    </svg>
  );
}

function TakeDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 3l18 18M10.6 5.1A9.8 9.8 0 0 1 12 5c5 0 9 4.5 10 7a13 13 0 0 1-2.6 3.8M6.2 6.2A13 13 0 0 0 2 12c1 2.5 5 7 10 7a9.6 9.6 0 0 0 4.4-1.1" />
    </svg>
  );
}
